import React, {
  KeyboardEvent,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import css from "@styled-system/css";
import {
  layout,
  LayoutProps,
  position,
  PositionProps,
  space,
  SpaceProps,
  border,
  BorderProps,
} from "styled-system";

import Box from "../Box/Box";
import Icon from "../Icon/Icon";
import {
  Placement,
  TooltipPosition,
  TooltipProps,
  TooltipCoordinates,
} from "./tooltip.type";

const arrowHeight = 6;
const defaultTooltipPosition: TooltipPosition = {
  tooltipTop: -1000,
  tooltipLeft: -1000,
  arrowTop: -1000,
  arrowLeft: -1000,
  arrowPlacement: "top",
};

const TriggerContainer = styled.div<LayoutProps & SpaceProps>`
  ${layout}
  ${space}
`;

const TooltipContainer = styled(Box)<PositionProps>`
  ${css({
    display: "block",
    backgroundColor: "background-default-color",
    borderRadius: 2,
    boxShadow: "hover",
    padding: 5,
  })}
  ${position}
`;

const Arrow = styled(Box)<PositionProps & BorderProps>`
  ${position}
  ${border}
`;

const Tooltip: React.FunctionComponent<TooltipProps> = ({
  id,
  children,
  placement = "top",
  customTrigger = null,
  zIndex = 2,
  tooltipTopOffset = 0,
  tooltipLeftOffset = 0,
  arrowTopOffset = 0,
  arrowLeftOffset = 0,
  tooltipSpacing = 12,
  delay = 0,
  maxWidth = 300,
}: TooltipProps) => {
  const [mouseOver, setMouseOver] = useState(false);
  const [tooltipContainer, setTooltipContainer] = useState(
    typeof window === "undefined" ? null : document.createElement("div")
  );
  const [triggerCoordinates, setTriggerCoordinates] =
    useState<TooltipCoordinates>({
      top: 0,
      left: 0,
      width: 0,
      height: 0,
    });
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>(
    defaultTooltipPosition
  );

  const tooltipRef = useRef(null);

  useEffect(() => {
    if (!tooltipContainer) {
      setTooltipContainer(document.createElement("div"));
      return;
    }
    document.body.appendChild(tooltipContainer);
    return () => {
      document.body.removeChild(tooltipContainer);
    };
  }, [tooltipContainer]);

  useEffect(() => {
    let timeout = 0;

    if (mouseOver) {
      timeout = window.setTimeout(() => {
        const tooltipPosition = getTooltipPosition({
          placement,
          tooltipRef,
          triggerCoordinates,
          tooltipSpacing,
        });

        setTooltipPosition({
          tooltipTop:
            triggerCoordinates.top -
            tooltipPosition.tooltipTop -
            tooltipTopOffset,
          tooltipLeft:
            triggerCoordinates.left -
            tooltipPosition.tooltipLeft -
            tooltipLeftOffset,
          arrowTop: tooltipPosition.arrowTop - arrowTopOffset,
          arrowLeft: tooltipPosition.arrowLeft - arrowLeftOffset,
          arrowPlacement: tooltipPosition.arrowPlacement as Placement,
        });
      }, delay);
    } else {
      setTooltipPosition(defaultTooltipPosition);
    }

    return () => clearTimeout(timeout);
  }, [
    triggerCoordinates,
    setTooltipPosition,
    mouseOver,
    placement,
    tooltipRef,
    tooltipTopOffset,
    tooltipLeftOffset,
    arrowTopOffset,
    arrowLeftOffset,
    tooltipSpacing,
    delay,
  ]);

  useEffect(() => {
    const handleScroll = () => {
      hideTooltip();
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const displayTooltip = (triggerNode: HTMLElement) => {
    const rect = triggerNode.getBoundingClientRect();

    setTriggerCoordinates({
      left: rect.x,
      top: rect.y,
      width: rect.width,
      height: rect.height,
    });
    setMouseOver(true);
  };

  const hideTooltip = () => setMouseOver(false);

  const handleKeyDown = (evt: KeyboardEvent<HTMLDivElement>) => {
    // esc key
    if (evt.keyCode !== 27) {
      return;
    }
    evt.preventDefault();
    hideTooltip();
  };

  const arrowProps = getArrowProps(tooltipPosition.arrowPlacement);

  return (
    <>
      <TriggerContainer
        onMouseEnter={(event) => displayTooltip(event.target as HTMLElement)}
        onMouseLeave={hideTooltip}
        onFocus={(event) => displayTooltip(event.target as HTMLElement)}
        onBlur={hideTooltip}
        onKeyDown={handleKeyDown}
        aria-describedby={id}
        tabIndex={0}
      >
        {Boolean(customTrigger) && customTrigger}
        {!Boolean(customTrigger) && (
          <Icon
            width={18}
            height={18}
            name="info-circle"
            weight={mouseOver ? "solid" : "regular"}
          />
        )}
      </TriggerContainer>
      {mouseOver &&
        tooltipContainer &&
        ReactDOM.createPortal(
          <TooltipContainer
            position="fixed"
            top={tooltipPosition.tooltipTop - tooltipTopOffset}
            left={tooltipPosition.tooltipLeft - tooltipLeftOffset}
            zIndex={zIndex}
            ref={tooltipRef}
            role="tooltip"
            id={id}
            maxWidth={maxWidth}
          >
            {children}
            <Arrow
              position="absolute"
              width={0}
              height={0}
              top={tooltipPosition.arrowTop}
              left={tooltipPosition.arrowLeft}
              {...arrowProps}
            />
          </TooltipContainer>,
          tooltipContainer
        )}
    </>
  );
};

const getTooltipPosition = ({
  placement,
  tooltipRef,
  triggerCoordinates,
  tooltipSpacing,
}: {
  placement: Placement;
  tooltipRef: RefObject<HTMLElement>;
  triggerCoordinates: TooltipCoordinates;
  tooltipSpacing: SpaceProps["margin"];
}) => {
  const tooltipNode = tooltipRef.current as unknown as HTMLElement;
  const tooltipStyle = tooltipNode?.getBoundingClientRect();
  const tooltipWidth = tooltipStyle?.width || 0;
  const tooltipHeight = tooltipStyle?.height || 0;
  const spacing = tooltipSpacing as number;
  const totalTooltipHeight = tooltipHeight + arrowHeight + spacing;
  const totalTooltipWidth = tooltipWidth + arrowHeight + spacing;

  const tooltipPosition = {
    top: {
      tooltipTop: tooltipHeight + arrowHeight + spacing,
      arrowTop: tooltipHeight,
      tooltipLeft: (tooltipWidth - triggerCoordinates.width) / 2,
      arrowLeft: tooltipWidth / 2 - arrowHeight,
      arrowPlacement: "top",
    },
    bottom: {
      tooltipTop: -(triggerCoordinates.height + arrowHeight + spacing),
      arrowTop: -arrowHeight,
      tooltipLeft: (tooltipWidth - triggerCoordinates.width) / 2,
      arrowLeft: tooltipWidth / 2 - arrowHeight,
      arrowPlacement: "bottom",
    },
    left: {
      tooltipTop: (tooltipHeight - triggerCoordinates.height) / 2,
      tooltipLeft: tooltipWidth + arrowHeight + spacing,
      arrowTop: tooltipHeight / 2 - arrowHeight,
      arrowLeft: tooltipWidth,
      arrowPlacement: "left",
    },
    right: {
      tooltipTop: (tooltipHeight - triggerCoordinates.height) / 2,
      tooltipLeft: -triggerCoordinates.width - arrowHeight - spacing,
      arrowTop: tooltipHeight / 2 - arrowHeight,
      arrowLeft: -arrowHeight,
      arrowPlacement: "right",
    },
  };

  if (placement === "top") {
    return triggerCoordinates.top >= totalTooltipHeight
      ? tooltipPosition[placement]
      : tooltipPosition["bottom"];
  }

  if (placement === "bottom") {
    return window.innerHeight - triggerCoordinates.top >= totalTooltipHeight
      ? tooltipPosition[placement]
      : tooltipPosition["top"];
  }

  if (placement === "left") {
    return triggerCoordinates.left >= totalTooltipWidth
      ? tooltipPosition[placement]
      : tooltipPosition["right"];
  }

  return window.innerWidth - triggerCoordinates.left >= totalTooltipWidth
    ? tooltipPosition[placement]
    : tooltipPosition["left"];
};

const getArrowProps = (placement: Placement) => {
  if (placement === "top") {
    return {
      borderTop: `${arrowHeight}px solid white;`,
      borderLeft: `${arrowHeight}px solid transparent;`,
      borderRight: `${arrowHeight}px solid transparent;`,
    };
  }
  if (placement === "bottom") {
    return {
      borderBottom: `${arrowHeight}px solid white;`,
      borderLeft: `${arrowHeight}px solid transparent;`,
      borderRight: `${arrowHeight}px solid transparent;`,
    };
  }
  if (placement === "left") {
    return {
      borderLeft: `${arrowHeight}px solid white;`,
      borderTop: `${arrowHeight}px solid transparent;`,
      borderBottom: `${arrowHeight}px solid transparent;`,
    };
  }

  return {
    borderRight: `${arrowHeight}px solid white;`,
    borderTop: `${arrowHeight}px solid transparent;`,
    borderBottom: `${arrowHeight}px solid transparent;`,
  };
};

export default Tooltip;
